import { hashKey, useQuery } from '@tanstack/react-query';
import { _bumpPerf } from '../../devtool/perf-counters.js';
import { buildFollowRow } from '../../helpers/apply-follow-optimistic.js';
import { hashDiagnosticEntity } from '../../tx-engine/diagnostics/lifecycle.js';
import { emit } from '../events.js';
import { writeNormalizedEntitySnapshot, } from './normalized-entity-write.js';
import { createSubscriberQueryFn, getSubscriberQueryMetaOptions } from './subscriber-query-fn.js';
const MISSING_VIEWER_RELATIONSHIP_INDEX_KEY = ['relationships', 'viewer', 'missing-index'];
const MISSING_VIEWER_RELATIONSHIP_POINT_KEY = ['relationships', 'viewer', 'missing-point'];
const activeRollbackTokens = new WeakMap();
export function viewerRelationshipQueryKey(scope, kind, targetId) {
    return ['relationships', 'viewer', scope, 'relation', kind, targetId];
}
export function viewerRelationshipScope(viewerScope, networkScope) {
    if (viewerScope.length === 0)
        return '';
    return hashKey([viewerScope, networkScope || 'unbound']);
}
export function viewerRelationshipIndexQueryKey(scope, kind, sourceQueryKey) {
    return ['relationships', 'viewer', scope, 'index', kind, hashKey(sourceQueryKey)];
}
export function useViewerRelationshipIndexIds(queryClient, indexKey) {
    const effectiveKey = indexKey ?? MISSING_VIEWER_RELATIONSHIP_INDEX_KEY;
    const result = useQuery({
        queryKey: effectiveKey,
        queryFn: createSubscriberQueryFn(queryClient, effectiveKey),
        ...getSubscriberQueryMetaOptions(queryClient, effectiveKey),
        enabled: false,
        staleTime: Number.POSITIVE_INFINITY,
        select: (index) => index.ids,
    });
    return indexKey === undefined ? undefined : result.data;
}
export function useViewerRelationshipRow(queryClient, queryKey) {
    const effectiveKey = queryKey ?? MISSING_VIEWER_RELATIONSHIP_POINT_KEY;
    const result = useQuery({
        queryKey: effectiveKey,
        queryFn: createSubscriberQueryFn(queryClient, effectiveKey),
        ...getSubscriberQueryMetaOptions(queryClient, effectiveKey),
        enabled: false,
        staleTime: Number.POSITIVE_INFINITY,
    });
    return queryKey === undefined ? undefined : result.data;
}
export function viewerRelationshipTarget(spec, args) {
    const targetArg = spec.point?.targetArg;
    if (targetArg === undefined)
        return undefined;
    return readNonEmptyString(args[targetArg]);
}
export function canMaterializeViewerRelationshipCollection(spec, args, viewerAccountId) {
    const collection = spec.collection;
    if (collection === undefined)
        return false;
    if (collection.conditionArg !== undefined &&
        args[collection.conditionArg] !== collection.conditionValue) {
        return false;
    }
    if (collection.ownerMatchesAuth !== true)
        return true;
    const ownerArg = collection.ownerArg;
    return ownerArg !== undefined && viewerAccountId !== null && args[ownerArg] === viewerAccountId;
}
export function writeViewerRelationship(queryClient, scope, kind, targetId, active, meta, row) {
    if (scope.length === 0 || targetId.length === 0) {
        recordDiagnostic({
            kind: 'scope-mismatch',
            relationship: kind,
            entityHash: hashDiagnosticEntity(targetId),
        });
        return { status: 'stale' };
    }
    const queryKey = viewerRelationshipQueryKey(scope, kind, targetId);
    const previous = queryClient.getQueryData(queryKey);
    const result = writeNormalizedEntitySnapshot(queryClient, queryKey, {
        targetId,
        active,
        ...(row === undefined ? (previous?.row === undefined ? {} : { row: previous.row }) : { row }),
    }, meta);
    if (result.status === 'written')
        clearRollbackToken(queryClient, queryKey);
    if (result.status === 'stale') {
        recordDiagnostic({
            kind: 'stale-write',
            relationship: kind,
            entityHash: hashDiagnosticEntity(targetId),
        });
    }
    return result;
}
export function writeViewerRelationshipCollection(queryClient, sourceQueryKey, scope, spec, args, data, viewerAccountId, meta, mode = 'replace') {
    if (!canMaterializeViewerRelationshipCollection(spec, args, viewerAccountId))
        return;
    const collection = spec.collection;
    if (collection === undefined || data === null || typeof data !== 'object')
        return;
    const indexValue = collection.indexArg === undefined ? undefined : readNonEmptyString(args[collection.indexArg]);
    if (collection.indexArg !== undefined && indexValue === undefined) {
        recordDiagnostic({
            kind: 'index-update-failed',
            relationship: spec.kind,
            entityHash: hashDiagnosticEntity(hashKey(sourceQueryKey)),
        });
        return;
    }
    const indexKey = viewerRelationshipIndexQueryKey(scope, spec.kind, sourceQueryKey);
    const entries = collectRelationshipEntries(data, collection);
    if (entries === undefined) {
        recordDiagnostic({
            kind: 'index-update-failed',
            relationship: spec.kind,
            entityHash: hashDiagnosticEntity(hashKey(sourceQueryKey)),
        });
        return;
    }
    for (const entry of entries) {
        writeViewerRelationship(queryClient, scope, spec.kind, entry.targetId, true, meta, entry.row);
    }
    const existing = queryClient.getQueryData(indexKey);
    const incomingIds = entries.map((entry) => entry.targetId);
    const ids = mode === 'append' ? mergeIndexIds(existing?.ids ?? [], incomingIds) : incomingIds;
    writeNormalizedEntitySnapshot(queryClient, indexKey, {
        ids,
        nextCursor: readNullableString(data.nextCursor),
        hasMore: data.hasMore === true,
        ...(collection.indexArg === undefined ? {} : { indexArg: collection.indexArg, indexValue }),
    }, meta);
}
export function materializeViewerRelationshipData(queryClient, sourceQueryKey, scope, spec, args, data) {
    if (data === null || typeof data !== 'object')
        return data;
    if (spec.point !== undefined) {
        const targetId = viewerRelationshipTarget(spec, args);
        if (targetId === undefined)
            return data;
        const relationship = queryClient.getQueryData(viewerRelationshipQueryKey(scope, spec.kind, targetId));
        if (relationship === undefined)
            return data;
        return {
            ...data,
            [spec.point.valueField]: relationship.active,
        };
    }
    const index = queryClient.getQueryData(viewerRelationshipIndexQueryKey(scope, spec.kind, sourceQueryKey));
    if (index === undefined)
        return data;
    const collection = spec.collection;
    if (collection === undefined)
        return data;
    if (collection.listField !== undefined) {
        return { ...data, [collection.listField]: index.ids };
    }
    const dataRecord = data;
    const rows = Array.isArray(dataRecord.data) ? dataRecord.data : [];
    const rowsByTarget = new Map();
    for (const row of rows) {
        if (row === null || typeof row !== 'object')
            continue;
        const targetId = readPath(row, collection.rowTargetPath ?? '');
        if (targetId !== undefined)
            rowsByTarget.set(targetId, row);
    }
    const materialized = [];
    for (const targetId of index.ids) {
        const relationship = queryClient.getQueryData(viewerRelationshipQueryKey(scope, spec.kind, targetId));
        if (relationship?.active !== true)
            continue;
        const row = relationship.row ?? rowsByTarget.get(targetId);
        if (row !== undefined)
            materialized.push(row);
    }
    return {
        ...data,
        data: materialized,
        nextCursor: index.nextCursor,
        hasMore: index.hasMore,
    };
}
export function applyViewerRelationshipMutation(queryClient, scope, spec, vars, meta) {
    const targetId = readNonEmptyString(vars[spec.targetArg]);
    const indexValue = spec.indexArg === undefined ? undefined : readNonEmptyString(vars[spec.indexArg]);
    if (targetId === undefined ||
        scope.length === 0 ||
        (spec.indexArg !== undefined && indexValue === undefined)) {
        recordDiagnostic({
            kind: 'scope-mismatch',
            relationship: spec.kind,
            entityHash: hashDiagnosticEntity(targetId ?? spec.targetArg),
        });
        return undefined;
    }
    const prefix = ['relationships', 'viewer', scope];
    const relationKey = viewerRelationshipQueryKey(scope, spec.kind, targetId);
    const previousRelation = queryClient.getQueryData(relationKey);
    // A first follow has no REST list row yet. Seed its public identity so the
    // new membership can render immediately in an already mounted list.
    const row = spec.kind === 'follow' && spec.active && previousRelation?.row === undefined
        ? { ...buildFollowRow(queryClient, targetId) }
        : undefined;
    const result = writeViewerRelationship(queryClient, scope, spec.kind, targetId, spec.active, meta, row);
    if (result.status === 'stale')
        return undefined;
    const token = {};
    setRollbackToken(queryClient, relationKey, token);
    const indexChanges = [];
    for (const [queryKey, value] of queryClient.getQueriesData({
        queryKey: prefix,
    })) {
        if (queryKey[3] !== 'index' || queryKey[4] !== spec.kind || value === undefined)
            continue;
        if (spec.indexArg !== undefined &&
            (value.indexArg !== spec.indexArg || value.indexValue !== indexValue)) {
            continue;
        }
        const previousIndex = value.ids.indexOf(targetId);
        const ids = spec.active
            ? previousIndex >= 0
                ? value.ids
                : [targetId, ...value.ids]
            : value.ids.filter((id) => id !== targetId);
        if (ids === value.ids || ids.length === value.ids.length)
            continue;
        const indexResult = writeNormalizedEntitySnapshot(queryClient, queryKey, { ...value, ids }, meta);
        if (indexResult.status === 'written') {
            indexChanges.push({ key: queryKey, previousIndex });
        }
    }
    return {
        indexChanges,
        kind: spec.kind,
        previousRelation,
        relationKey,
        targetId,
        token,
    };
}
export function rollbackViewerRelationshipMutation(queryClient, rollback) {
    if (rollback === undefined)
        return;
    if (getRollbackToken(queryClient, rollback.relationKey) !== rollback.token) {
        recordDiagnostic({
            kind: 'rollback-superseded',
            relationship: rollback.kind,
            entityHash: hashDiagnosticEntity(rollback.targetId),
        });
        return;
    }
    deleteRollbackToken(queryClient, rollback.relationKey);
    if (rollback.previousRelation === undefined) {
        queryClient.removeQueries({ queryKey: rollback.relationKey, exact: true }); // ALLOW-STATE-OWNERSHIP: exact relationship cleanup
    }
    else {
        // ALLOW: the relationship runtime owns this exact viewer entity.
        // REASON: rollback restores only the supersession-checked mutation target.
        queryClient.setQueryData(rollback.relationKey, rollback.previousRelation); // ALLOW-STATE-OWNERSHIP: exact relationship rollback
    }
    for (const change of rollback.indexChanges) {
        const restoreIndex = (current) => {
            if (current === undefined)
                return current;
            const withoutTarget = current.ids.filter((id) => id !== rollback.targetId);
            if (change.previousIndex < 0) {
                return withoutTarget.length === current.ids.length
                    ? current
                    : { ...current, ids: withoutTarget };
            }
            const insertionIndex = Math.min(change.previousIndex, withoutTarget.length);
            const ids = [
                ...withoutTarget.slice(0, insertionIndex),
                rollback.targetId,
                ...withoutTarget.slice(insertionIndex),
            ];
            return { ...current, ids };
        };
        // ALLOW: the relationship runtime owns membership in declared list indexes.
        // REASON: rollback edits only this target ID in indexes touched by the mutation.
        queryClient.setQueryData(change.key, restoreIndex); // ALLOW-STATE-OWNERSHIP: targeted index rollback
    }
}
export function recordViewerRelationshipRecovery(relationship, targetId) {
    recordDiagnostic({
        kind: 'missing-recovery',
        relationship,
        entityHash: hashDiagnosticEntity(targetId),
    });
}
function collectRelationshipEntries(data, collection) {
    if (collection.listField !== undefined) {
        const ids = data[collection.listField];
        if (!Array.isArray(ids))
            return undefined;
        return ids.flatMap((id) => {
            const targetId = readNonEmptyString(id);
            return targetId === undefined ? [] : [{ targetId }];
        });
    }
    const rows = data.data;
    const rowTargetPath = collection.rowTargetPath;
    if (!Array.isArray(rows) || rowTargetPath === undefined)
        return undefined;
    return rows.flatMap((row) => {
        if (row === null || typeof row !== 'object')
            return [];
        const targetId = readPath(row, rowTargetPath);
        return targetId === undefined ? [] : [{ targetId, row: row }];
    });
}
function mergeIndexIds(existing, incoming) {
    const seen = new Set();
    const ids = [];
    for (const id of [...existing, ...incoming]) {
        if (seen.has(id))
            continue;
        seen.add(id);
        ids.push(id);
    }
    return ids;
}
function readPath(row, path) {
    let value = row;
    for (const part of path.split('.')) {
        if (value === null || typeof value !== 'object')
            return undefined;
        value = value[part];
    }
    return readNonEmptyString(value);
}
function readNonEmptyString(value) {
    return typeof value === 'string' && value.length > 0 ? value : undefined;
}
function readNullableString(value) {
    return typeof value === 'string' && value.length > 0 ? value : null;
}
function rollbackTokens(queryClient) {
    let tokens = activeRollbackTokens.get(queryClient);
    if (tokens === undefined) {
        tokens = new Map();
        activeRollbackTokens.set(queryClient, tokens);
    }
    return tokens;
}
function clearRollbackToken(queryClient, queryKey) {
    activeRollbackTokens.get(queryClient)?.delete(hashKey(queryKey));
}
function setRollbackToken(queryClient, queryKey, token) {
    rollbackTokens(queryClient).set(hashKey(queryKey), token);
}
function getRollbackToken(queryClient, queryKey) {
    return activeRollbackTokens.get(queryClient)?.get(hashKey(queryKey));
}
function deleteRollbackToken(queryClient, queryKey) {
    const tokens = activeRollbackTokens.get(queryClient);
    tokens?.delete(hashKey(queryKey));
    if (tokens?.size === 0)
        activeRollbackTokens.delete(queryClient);
}
function recordDiagnostic(diagnostic) {
    if (process.env.NODE_ENV !== 'production') {
        if (diagnostic.kind === 'scope-mismatch')
            _bumpPerf('viewerRelationship.scopeMismatches');
        if (diagnostic.kind === 'stale-write')
            _bumpPerf('viewerRelationship.staleWritesRejected');
        if (diagnostic.kind === 'index-update-failed')
            _bumpPerf('viewerRelationship.indexUpdateFailures');
    }
    emit('viewer-relationship:diagnostic', diagnostic);
}
//# sourceMappingURL=viewer-relationship.js.map